// HOW A CARD MOVES, AS PURE RULES: what the Operations Inbox's tick does to one
// card given what the work looks like now. No database: the tick reads the
// card and the kind's reading, asks here, and writes what comes back (the
// card, its history's new lines, and the day it closed), so every rule below
// is a test away from being checked.
//
// Two shapes of card:
//
//   COUNTING (the daily kinds): a target and a done count, the target live
//   until the first action of the day by someone in the card's seat, then
//   frozen; later arrivals are "+N new" beside it. Never late: at the day's
//   roll the same card takes the new day with the whole queue as its target,
//   and the day that ended is kept as one row of the reporting record.
//
//   ONE-OFF (launch a campaign, the weekly report): owed once in its period,
//   done by the first thing that does it, late after its due day.
//
//   WHILE OWED (the script cards, an onboarding section, the Client Comms
//   cards): to do while its reader finds work owed, due
//   on the oldest of it, so it goes late; done when none is left, and to do
//   again when work comes back (`stepOwed`).
//
//   AD HOC (typed in): no reader; a person adds it, snoozes it and marks it
//   done (`newTask`, `moveByHand`), and the tick only wakes it (`stepTask`).
//
// A PERSON'S MOVES on any card (snooze, archive and their undoing) are
// `moveByHand`, each with a history line that says who did what.
//
// ⭐ A SNOOZE IS ITS HISTORY LINES (lib/ops-inbox-snoozes.ts reads them back):
// `snoozed` (for how many days, until when), `snooze-moved`, `unsnoozed`
// (woke up at its day, or brought back early by hand), and a close while it
// slept (`done`, from a snoozed card). Each line's from / to carries
// `snoozed_until`, and a snooze's `days`, so "how long, how often" is a query
// over ops_card_event, with no table of its own.
//
// ⭐ ENDED WORK CLOSES ITS CARDS (`closeEnded`): a brand archived, or its
// onboarding checklist archived or deleted, closes its open cards with the
// reason, whoever and whenever the lifecycle's own row says.
//
// ⚠️ WORK DONE IN REACHER DIRECTLY COUNTS, AND IS REACHER'S. Reacher keeps no
// actor and we hold no time for it, so it is seen as a unit that was owed at one
// pass and has left the queue finished, with no act of ours behind it, at the
// next (`countUnits`). It counts in the done count like any unit, dated by the
// pass that noticed it, and a card it finishes is done via Reacher, credited to
// no person.
//
// ⚠️ A CARD IS NEVER MADE ALREADY DONE FOR WANT OF WORK. A counting kind with
// nothing owed makes no card; a card whose work runs out is marked done and
// keeps its day, so it does not show as today's.
//
// Shared, not server-only, and erasable TypeScript: the tick runs in a sidecar
// under node's type stripping.

import { comesBackFromArchive, kindDef } from './ops-inbox-kinds';
import { daysBetweenUtc, formatShort } from './schedule-dates';
import { operationsDay } from './throughput-operations';

export type CardState = 'todo' | 'done' | 'snoozed' | 'archived';
/** Where a card's work was done: in the app (by someone we can name), or in Reacher directly (by no one we can). */
export type DoneVia = 'app' | 'reacher';

/**
 * What a card shows beyond its counter (`detail_json`): numbers and short codes,
 * and for a kind that lists its work, one short list (review scripts' scripts).
 * Readers check every value's type: a row is never trusted to have the shape.
 */
export type CardDetail = Record<string, unknown>;

/** An `ops_card` row, as the rules read and return it. */
export interface CardRow {
  cardId: string;
  kind: string;
  workstream: string | null;
  brandSlug: string | null;
  subject: string;
  seat: string;
  escalatedFrom: string | null;
  dueOn: string;
  state: CardState;
  target: number | null;
  doneN: number | null;
  frozenAt: number | null;
  doneBy: string | null;
  doneVia: DoneVia | null;
  doneAt: number | null;
  snoozedUntil: string | null;
  archivedAt: number | null;
  archivedBy: string | null;
  /** What the card shows beyond its counter (`detail_json`). */
  detail: CardDetail | null;
  createdAt: number;
  /** An ad-hoc card's own words; null on every other kind. Optional: the tick's cards are built without them. */
  title?: string | null;
  body?: string | null;
}

/** An `ops_card` row as SQLite hands it back (`SELECT *`), before `cardFromDb` reads it. */
export interface CardDb {
  card_id: string;
  kind: string;
  workstream: string | null;
  brand_slug: string | null;
  subject: string;
  seat: string;
  escalated_from: string | null;
  due_on: string;
  state: CardState;
  target: number | null;
  done_n: number | null;
  frozen_at: number | null;
  done_by: string | null;
  done_via: DoneVia | null;
  done_at: number | null;
  snoozed_until: string | null;
  archived_at: number | null;
  archived_by: string | null;
  detail_json: string | null;
  title?: string | null;
  body?: string | null;
  created_at: number;
}

/** `detail_json` read back: an object, or null for nothing or for anything that is not one. */
function detailOf(raw: string | null): CardRow['detail'] {
  if (raw === null) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as CardDetail) : null;
  } catch {
    return null;
  }
}

/** One `ops_card` row as the rules and the page read it. The tick and the inbox both read through this, so a column is spelled once. */
export const cardFromDb = (r: CardDb): CardRow => ({
  cardId: r.card_id,
  kind: r.kind,
  workstream: r.workstream,
  brandSlug: r.brand_slug,
  subject: r.subject,
  seat: r.seat,
  escalatedFrom: r.escalated_from,
  dueOn: r.due_on,
  state: r.state,
  target: r.target,
  doneN: r.done_n,
  frozenAt: r.frozen_at,
  doneBy: r.done_by,
  doneVia: r.done_via,
  doneAt: r.done_at,
  snoozedUntil: r.snoozed_until,
  archivedAt: r.archived_at,
  archivedBy: r.archived_by,
  detail: detailOf(r.detail_json),
  createdAt: r.created_at,
  title: r.title ?? null,
  body: r.body ?? null,
});

/** A line for `ops_card_event`. `actor` null: the tick did it. `note`: why, in words, where the kind says (an overdue card's timeline). */
export interface CardEvent {
  verb: string;
  actor: string | null;
  at: number;
  from: Record<string, unknown> | null;
  to: Record<string, unknown> | null;
  note?: string | null;
}

/** One card's move: the card as it now is, the history to append, and the day it closed (a counting card's roll). */
export interface CardStep {
  card: CardRow;
  events: CardEvent[];
  closedDay: { day: string; target: number; doneN: number; reacherN: number } | null;
}

/** Who did something, and when. */
export interface Act {
  actor: string;
  at: number;
}

/** A new card's fixed parts: what the tick knows before there is a row. */
export interface CardSeed {
  cardId: string;
  kind: string;
  workstream: string | null;
  brandSlug: string | null;
  subject: string;
  /** The seat a brand's card belongs on now: its pod's Ops seat. */
  seat: string;
}

/** What a counting card's work looks like now: what `stepCounting` reads. */
export interface CountingReading {
  /** Units owed now (creators waiting, conversations unanswered). */
  owed: number;
  /** Units handled since the day began, in the app or in Reacher: the queue being worked, not anyone's tally. */
  doneToday: number;
  /** Of `doneToday`, the units done in Reacher directly. */
  inReacher: number;
  /** When the latest of those was noticed: the pass's time, not the work's, which no read keeps. */
  reacherAt: number | null;
  /** Today's first act in the app by someone in the card's seat (or the seat it was escalated from): the freeze. */
  firstSeatAct: Act | null;
  /** Today's latest act in the app, by anyone. */
  lastAct: Act | null;
  /** The kind's own numbers for the card (the triage's yes / look / no). */
  extra: Record<string, number>;
}

/** One act in the app on one unit: a creator decided, a conversation answered. */
export interface UnitAct {
  unit: string;
  actor: string;
  at: number;
}

/**
 * WHAT A COUNTING KIND'S READER FOUND, before it is set against the card's
 * last pass: the queue now, today's acts in the app, and which units the read
 * shows finished outside the app.
 */
export interface UnitsReading {
  /** Units owed now, the ones a later pass can't name (Reacher's unlisted conversations) included. */
  owed: number;
  /** The owed units we can name: a later pass sees one leave. */
  owedUnits: readonly string[];
  /**
   * Units the read shows finished with no act of ours behind them (approved or
   * declined in Reacher, answered there). Never one that merely went away: a
   * request that expired or was withdrawn, a conversation gone stale.
   */
  doneElsewhere: ReadonlySet<string>;
  /** Today's acts in the app. */
  acts: readonly UnitAct[];
  extra: Record<string, number>;
}

/** A counting card's units as its last pass left them: owed then, and those done in Reacher today, when noticed. */
export interface UnitLedger {
  owed: ReadonlySet<string>;
  reacherToday: ReadonlyMap<string, number>;
}

export const EMPTY_LEDGER: UnitLedger = { owed: new Set(), reacherToday: new Map() };

/**
 * THE READING, WITH WHAT REACHER DID SINCE THE LAST PASS. A unit owed at the
 * last pass that is no longer owed, that the read shows finished, and that no
 * act of ours today touched, was done in Reacher: it is noticed now, at `at`.
 * A unit owed again (an Undo, a new request) is no longer done anywhere. The
 * done count is every unit acted on in the app today or done in Reacher today,
 * each once.
 */
export function countUnits(u: UnitsReading, ledger: UnitLedger, inSeat: (email: string) => boolean, at: number): { reading: CountingReading; ledger: UnitLedger } {
  const owedNow = new Set(u.owedUnits);
  const acted = new Set(u.acts.map((a) => a.unit));
  const reacherToday = new Map<string, number>();
  for (const [unit, when] of ledger.reacherToday) if (!owedNow.has(unit)) reacherToday.set(unit, when);
  for (const unit of ledger.owed) {
    if (!owedNow.has(unit) && !acted.has(unit) && u.doneElsewhere.has(unit)) reacherToday.set(unit, at);
  }
  let first: Act | null = null;
  let last: Act | null = null;
  for (const a of u.acts) {
    if (inSeat(a.actor) && (first === null || a.at < first.at)) first = { actor: a.actor, at: a.at };
    if (last === null || a.at > last.at) last = { actor: a.actor, at: a.at };
  }
  let inReacher = 0;
  let reacherAt: number | null = null;
  for (const when of reacherToday.values()) {
    inReacher += 1;
    if (reacherAt === null || when > reacherAt) reacherAt = when;
  }
  return {
    reading: { owed: u.owed, doneToday: acted.size + inReacher, inReacher, reacherAt, firstSeatAct: first, lastAct: last, extra: u.extra },
    ledger: { owed: owedNow, reacherToday },
  };
}

/** What a one-off card's reader found for its subject: the first thing that did the work, if anything has (no actor: Reacher). */
export interface OneOffReading {
  done: { actor: string | null; at: number; via: DoneVia } | null;
  extra: CardDetail;
  /** Why, in words, for the history line of each move ("opened: the report is Not started"); none: the line has no note. */
  notes?: { made?: string; done?: string };
}

const snap = (c: CardRow): Record<string, unknown> => ({ state: c.state, due_on: c.dueOn, seat: c.seat, target: c.target, done_n: c.doneN });
/** A snooze line's card: `snap` with the day it sleeps until, so a snooze's spans read back from the history alone. */
const snoozeSnap = (c: CardRow): Record<string, unknown> => ({ ...snap(c), snoozed_until: c.snoozedUntil });
/** "(until Nov 6)", for a line written while a card sleeps; empty for one with no day. */
const untilWords = (c: Pick<CardRow, 'snoozedUntil'>): string => (c.snoozedUntil === null ? '' : ` (until ${formatShort(c.snoozedUntil)})`);
const daysWords = (n: number): string => `${n} day${n === 1 ? '' : 's'}`;

const sameDetail = (a: CardRow['detail'], b: CardRow['detail']): boolean => JSON.stringify(a) === JSON.stringify(b);

function changed(a: CardRow, b: CardRow): boolean {
  return (
    a.seat !== b.seat ||
    a.dueOn !== b.dueOn ||
    a.state !== b.state ||
    a.target !== b.target ||
    a.doneN !== b.doneN ||
    a.frozenAt !== b.frozenAt ||
    a.doneBy !== b.doneBy ||
    a.doneVia !== b.doneVia ||
    a.doneAt !== b.doneAt ||
    a.snoozedUntil !== b.snoozedUntil ||
    a.archivedAt !== b.archivedAt ||
    a.archivedBy !== b.archivedBy ||
    !sameDetail(a.detail, b.detail)
  );
}

/** Whether a snoozed card's snooze is over on `day`: its day has come (or it names none). The tick wakes it then; the page reads it as to do from that day, before the tick has. */
export const snoozeOver = (c: Pick<CardRow, 'state' | 'snoozedUntil'>, day: string): boolean =>
  c.state === 'snoozed' && (c.snoozedUntil === null || c.snoozedUntil <= day);

/**
 * A CARD AS OF A DAY: one whose snooze is over is to do from its day on, in
 * the minutes before the tick wakes it (and writes the line that says so). The
 * page draws, and a person moves, this card.
 */
export const asOfDay = (c: CardRow, day: string): CardRow => (snoozeOver(c, day) ? { ...c, state: 'todo', snoozedUntil: null } : c);

/**
 * A SNOOZED OR ARCHIVED CARD'S WAKE, the same for every kind: a snoozed one
 * is left alone until its day, then is to do again ("woke up"; a while-owed
 * card whose work ended while it slept is closed by `stepOwed` instead); an archived one comes back
 * by its kind's rule (lib/ops-inbox-kinds.ts `comesBackFromArchive`), or is
 * left alone. Null: leave the card be this pass. `back` is the history line's
 * why for an archived card that comes back ("a new day, 4 waiting").
 */
function wake(card: CardRow, day: string, at: number, workOwedSince: number | null, events: CardEvent[], back: string | null = null): CardRow | null {
  if (card.state === 'snoozed') {
    if (!snoozeOver(card, day)) return null;
    const next: CardRow = { ...card, state: 'todo', snoozedUntil: null };
    const until = card.snoozedUntil === null ? '' : ` (snoozed until ${formatShort(card.snoozedUntil)})`;
    events.push({ verb: 'unsnoozed', actor: null, at, from: snoozeSnap(card), to: snoozeSnap(next), note: `woke up${until}` });
    return next;
  }
  if (card.state === 'archived') {
    if (card.archivedAt === null || !comesBackFromArchive(card.kind, { archivedAt: card.archivedAt }, { at, newestWorkAt: workOwedSince })) return null;
    // Back as work to do, whatever it was when archived: what brought it back is work owed now.
    const next: CardRow = { ...reopen(card), archivedAt: null, archivedBy: null, snoozedUntil: null };
    events.push({ verb: 'unarchived', actor: null, at, from: snap(card), to: snap(next), note: back === null ? 'brought back: new work since it was archived' : `brought back: ${back}` });
    return next;
  }
  return card;
}

/**
 * A BRAND THAT CHANGED POD TAKES ITS OPEN CARDS WITH IT, to its new pod's Ops
 * seat; a done card stays where its work was done, and an escalated one stays
 * with whoever it was escalated to.
 */
function reseat(card: CardRow, seat: string, at: number, events: CardEvent[]): CardRow {
  if (card.escalatedFrom !== null || card.seat === seat || card.state === 'done' || card.state === 'archived') return card;
  const next = { ...card, seat };
  events.push({ verb: 'moved', actor: null, at, from: snap(card), to: snap(next) });
  return next;
}

const fresh = (seed: CardSeed, day: string, at: number): CardRow => ({
  ...seed,
  escalatedFrom: null,
  dueOn: day,
  state: 'todo',
  target: null,
  doneN: null,
  frozenAt: null,
  doneBy: null,
  doneVia: null,
  doneAt: null,
  snoozedUntil: null,
  archivedAt: null,
  archivedBy: null,
  detail: null,
  createdAt: at,
});

/**
 * A counting card done: credited to whatever did today's latest work, an act
 * in the app (its person) or a unit done in Reacher (Reacher, no person). A
 * queue that ran out with neither behind it today ran out outside the app too.
 */
function markDone(c: CardRow, r: CountingReading, at: number): CardRow {
  const app = r.lastAct;
  if (app !== null && (r.reacherAt === null || app.at >= r.reacherAt)) return { ...c, state: 'done', doneBy: app.actor, doneVia: 'app', doneAt: app.at };
  return { ...c, state: 'done', doneBy: null, doneVia: 'reacher', doneAt: r.reacherAt ?? at };
}

const detailN = (d: CardRow['detail'], k: string): number => {
  const v = d?.[k];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
};

const reopen = (c: CardRow): CardRow => ({ ...c, state: 'todo', doneBy: null, doneVia: null, doneAt: null });

/**
 * ONE PASS OVER A DAILY COUNTING CARD (review sample requests, review
 * messages). `card` null: none exists for the subject yet. `day` is today's
 * `operationsDay`; `at` the pass's time. Null: nothing to write.
 *
 *   make      only when something is owed
 *   roll      a card due on an earlier day takes today (so it is never late),
 *             with the whole queue as its target and the freeze cleared; the
 *             day that ended is closed (`closedDay`). With nothing owed it is
 *             marked done and keeps its day: it is not today's work
 *   freeze    today's first act by someone in the seat fixes the target; an
 *             act by anyone else, or work done in Reacher, never does
 *   count     the done count is today's units done in the app or in Reacher
 *             (`countUnits`); past the freeze, the units owed beyond the target
 *             are "+N new"
 *   done      when nothing is owed, or, frozen, when the done count reaches the
 *             target; credited to today's latest work: its person in the app,
 *             or Reacher
 *   reopen    a done card whose work came back undone (an Undo, or new
 *             arrivals before any freeze) is to do again
 */
export function stepCounting(
  card: CardRow | null,
  seed: CardSeed,
  r: CountingReading,
  day: string,
  at: number,
  /** The card's due day already has its `ops_card_day` row: a done card waiting for work closes it once, not every pass. */
  dayKept = false,
): CardStep | null {
  const events: CardEvent[] = [];
  let closedDay: CardStep['closedDay'] = null;
  let c: CardRow;
  if (card === null) {
    if (r.owed <= 0) return null;
    c = fresh(seed, day, at);
    events.push({ verb: 'made', actor: null, at, from: null, to: { state: 'todo', due_on: day, seat: seed.seat } });
  } else {
    const woken = wake(card, day, at, r.owed > 0 ? at : null, events, `a new day, ${r.owed.toLocaleString('en-US')} waiting`);
    if (woken === null) return null;
    c = reseat(woken, seed.seat, at, events);
    if (c.dueOn < day) {
      // Close the day the card was due, once.
      if (c.target !== null && !dayKept) closedDay = { day: c.dueOn, target: c.target, doneN: c.doneN ?? 0, reacherN: detailN(c.detail, 'inReacher') };
      if (r.owed <= 0) {
        if (c.state === 'todo') {
          const next = markDone(c, r, at);
          events.push({ verb: 'done', actor: next.doneBy, at, from: snap(c), to: { ...snap(next), done_via: next.doneVia } });
          c = next;
        }
        return changed(card, c) || events.length > 0 || closedDay !== null ? { card: c, events, closedDay } : null;
      }
      const next: CardRow = { ...c, dueOn: day, state: 'todo', frozenAt: null, target: null, doneN: null, doneBy: null, doneVia: null, doneAt: null };
      events.push({ verb: 'due-moved', actor: null, at, from: snap(c), to: { due_on: day, state: 'todo' } });
      c = next;
    }
  }

  const live = r.owed + r.doneToday;
  if (c.frozenAt === null && r.firstSeatAct !== null) {
    c = { ...c, frozenAt: r.firstSeatAct.at, target: live };
    events.push({ verb: 'frozen', actor: r.firstSeatAct.actor, at, from: null, to: { target: live } });
  }
  const frozen = c.frozenAt !== null;
  const target = frozen && c.target !== null ? c.target : live;
  c = {
    ...c,
    target,
    doneN: r.doneToday,
    detail: { ...r.extra, owed: r.owed, newSinceFreeze: frozen ? Math.max(0, live - target) : 0, inReacher: r.inReacher },
  };

  const finished = r.owed <= 0 || (frozen && r.doneToday >= target);
  if (c.state === 'todo' && finished) {
    const next = markDone(c, r, at);
    events.push({ verb: 'done', actor: next.doneBy, at, from: snap(c), to: { ...snap(next), done_via: next.doneVia } });
    c = next;
  } else if (c.state === 'done' && !finished) {
    const next = reopen(c);
    events.push({ verb: 'reopened', actor: null, at, from: snap(c), to: snap(next) });
    c = next;
  }

  if (card !== null && !changed(card, c) && events.length === 0 && closedDay === null) return null;
  return { card: c, events, closedDay };
}

/**
 * ONE PASS OVER AN AD-HOC TASK: nothing reads its work, so the tick only
 * wakes it when its snooze is over (an archived one comes back only by hand).
 * Null: nothing to write.
 */
export function stepTask(card: CardRow, day: string, at: number): CardStep | null {
  const events: CardEvent[] = [];
  const woken = wake(card, day, at, null, events);
  if (woken === null || events.length === 0) return null;
  return { card: woken, events, closedDay: null };
}

/**
 * ONE PASS OVER A ONE-OFF CARD (launch a campaign): owed once in its period,
 * due on `dueOn`, late after it. Made for every subject the tick names, done
 * already when the work was done before the card was made (it was owed, and
 * the record says so). A done card stays done: nothing it did is taken back.
 */
export function stepOneOff(card: CardRow | null, seed: CardSeed, r: OneOffReading, dueOn: string, day: string, at: number): CardStep | null {
  const events: CardEvent[] = [];
  let c: CardRow;
  if (card === null) {
    c = { ...fresh(seed, dueOn, at), detail: { ...r.extra } };
    events.push({ verb: 'made', actor: null, at, from: null, to: { state: 'todo', due_on: dueOn, seat: seed.seat }, note: r.notes?.made ?? null });
  } else {
    const woken = wake(card, day, at, null, events);
    if (woken === null) return null;
    c = reseat(woken, seed.seat, at, events);
    c = { ...c, detail: { ...r.extra } };
    if (c.state === 'todo' && c.dueOn !== dueOn) {
      events.push({ verb: 'due-moved', actor: null, at, from: snap(c), to: { due_on: dueOn } });
      c = { ...c, dueOn };
    }
  }
  if (c.state === 'todo' && r.done !== null) {
    const next: CardRow = { ...c, state: 'done', doneBy: r.done.actor, doneVia: r.done.via, doneAt: r.done.at };
    events.push({ verb: 'done', actor: r.done.actor, at, from: snap(c), to: { ...snap(next), done_via: r.done.via }, note: r.notes?.done ?? null });
    c = next;
  }
  if (card !== null && !changed(card, c) && events.length === 0) return null;
  return { card: c, events, closedDay: null };
}

/**
 * WHAT A WHILE-OWED CARD'S READER FOUND for its subject (review scripts,
 * overdue scripts): the work owed now, when it is due, and what finished it.
 */
export interface OwedReading {
  /** Units owed now (scripts awaiting review, late deliverables); 0: nothing is. */
  owed: number;
  /** The day the card is due while work is owed: the oldest owed work's. */
  dueOn: string;
  /** A counter, for a kind that draws one (scripts reviewed of those sent); null for one that doesn't. */
  target: number | null;
  doneN: number | null;
  /** With nothing owed, what finished the work: its person when one is known. Null: no one we can name, in the app. */
  done: { actor: string | null; at: number; via: DoneVia } | null;
  /** When the newest of the owed work began (ms): an archived card comes back for work newer than its archive. */
  workSince: number | null;
  extra: CardDetail;
  /** Why, in words, for the history line of each move ("opened: 1 day late (2 scripts)"); none: the line has no note. */
  notes?: { made?: string; reopened?: string; closed?: string; back?: string };
}

/**
 * WHETHER A DONE CARD TAKES OWED WORK BACK UP NOW. One done because nothing
 * was owed takes any that comes back; one marked done with work still owed (a
 * reminder sent for a late creator) is done for that day and comes back the
 * next, if the work is still owed then.
 */
export function reopensNow(card: Pick<CardRow, 'detail' | 'doneAt'>, day: string): boolean {
  if (detailN(card.detail, 'owed') <= 0) return true;
  return card.doneAt === null || operationsDay(card.doneAt) < day;
}

/**
 * ONE PASS OVER A WHILE-OWED CARD. `card` null: none exists for the subject.
 * Null: nothing to write.
 *
 *   make      only when something is owed (a card is never made done)
 *   due       to do, it is due on the oldest owed work's day, so it ages into
 *             the late sections; done, it keeps the day it had
 *   done      when nothing is owed, explained by what the reader says ended
 *             the work (`done_by` says who did, not who gets credit); a card
 *             done for the day with its work still owed (a reminder sent) is
 *             closed again the same way once nothing is owed, so its last
 *             line is what ended the work, never the reminder
 *   reopen    a done card with work owed again, by `reopensNow`
 *   asleep    a snoozed card sleeps until its day while work is owed; when
 *             none is, it closes then, saying it was closed while snoozed
 *             (never woken first to be closed)
 *
 * A subject with nothing owed and no card gets none: never one made and
 * closed in one pass.
 */
export function stepOwed(card: CardRow | null, seed: CardSeed, r: OwedReading, day: string, at: number): CardStep | null {
  const events: CardEvent[] = [];
  let c: CardRow;
  if (card === null) {
    if (r.owed <= 0) return null;
    c = fresh(seed, r.dueOn, at);
    events.push({ verb: 'made', actor: null, at, from: null, to: { state: 'todo', due_on: r.dueOn, seat: seed.seat }, note: r.notes?.made ?? null });
  } else if (card.state === 'snoozed' && r.owed <= 0) {
    // The work ended while the card slept: it closes now, saying so, rather than waking to close.
    const by = r.done ?? { actor: null, at, via: 'app' as const };
    const why = (r.notes?.closed ?? '').replace(/^closed: /, '') || 'the work was done';
    const asleep: CardRow = { ...card, target: r.target, doneN: r.doneN, detail: { ...r.extra, owed: 0 } };
    const next = closedAsleep(asleep, by, day);
    events.push({ verb: 'done', actor: by.actor, at, from: snoozeSnap(card), to: { ...snoozeSnap(next), done_via: by.via }, note: `closed while snoozed${untilWords(card)}: ${why}` });
    return { card: next, events, closedDay: null };
  } else {
    const woken = wake(card, day, at, r.owed > 0 ? r.workSince : null, events, r.notes?.back ?? null);
    if (woken === null) return null;
    c = reseat(woken, seed.seat, at, events);
  }
  const was = c;
  c = { ...c, target: r.target, doneN: r.doneN, detail: { ...r.extra, owed: Math.max(0, r.owed) } };
  if (c.state === 'todo' && r.owed > 0 && c.dueOn !== r.dueOn) {
    events.push({ verb: 'due-moved', actor: null, at, from: snap(c), to: { due_on: r.dueOn } });
    c = { ...c, dueOn: r.dueOn };
  }
  // Done for the day with work still owed (`was` held it), and now nothing is: the work's own end closes it.
  const doneForTheDay = c.state === 'done' && detailN(was.detail, 'owed') > 0;
  if ((c.state === 'todo' || doneForTheDay) && r.owed <= 0) {
    const by = r.done ?? { actor: null, at, via: 'app' as const };
    const next: CardRow = { ...c, state: 'done', doneBy: by.actor, doneVia: by.via, doneAt: by.at };
    events.push({ verb: 'done', actor: by.actor, at, from: snap(c), to: { ...snap(next), done_via: by.via }, note: r.notes?.closed ?? null });
    c = next;
  } else if (c.state === 'done' && r.owed > 0 && reopensNow(was, day)) {
    const next: CardRow = { ...reopen(c), dueOn: r.dueOn };
    events.push({ verb: 'reopened', actor: null, at, from: snap(c), to: snap(next), note: r.notes?.reopened ?? null });
    c = next;
  }
  if (card !== null && !changed(card, c) && events.length === 0) return null;
  return { card: c, events, closedDay: null };
}

/** A sleeping card closed: done, awake, and due no later than today, since the snooze's day was never the work's. */
function closedAsleep(c: CardRow, by: { actor: string | null; at: number; via: DoneVia }, day: string): CardRow {
  return { ...c, state: 'done', snoozedUntil: null, dueOn: c.dueOn > day ? day : c.dueOn, doneBy: by.actor, doneVia: by.via, doneAt: by.at };
}

// ─── Ended work ─────────────────────────────────────────────────────────────

/** Why a card's work ended outside it: its brand archived, or its brand's onboarding checklist archived or deleted. */
export type EndedWhy = 'brand-archived' | 'checklist-archived' | 'checklist-deleted';
const ENDED: readonly EndedWhy[] = ['brand-archived', 'checklist-archived', 'checklist-deleted'];
export const endedOf = (v: unknown): EndedWhy | null => ((ENDED as readonly unknown[]).includes(v) ? (v as EndedWhy) : null);

/** The lifecycle row that ended a card's work: why, who (an email, when the row names one) and when (null: no row has the time). */
export interface EndedWork {
  why: EndedWhy;
  actor: string | null;
  at: number | null;
}

/** The ended work in a history line's words: "Organic India was archived by Dana", "Organic India's onboarding checklist was deleted". */
export function endedWords(why: EndedWhy, brandName: string, who: string | null): string {
  const by = who ? ` by ${who}` : '';
  switch (why) {
    case 'brand-archived': return `${brandName} was archived${by}`;
    case 'checklist-archived': return `${brandName}'s onboarding checklist was archived${by}`;
    case 'checklist-deleted': return `${brandName}'s onboarding checklist was deleted${by}`;
  }
}

/**
 * WHETHER THE TICK STILL OWES A CARD A CLOSE: to do, snoozed, or done for the
 * day with work still owed (an overdue card a reminder closed). Done for good
 * and archived cards are left as they are; so is an ad-hoc task, which a person
 * typed in and a person closes (an archived brand's offboarding is just such a
 * task).
 */
export const endsWithItsWork = (c: Pick<CardRow, 'kind' | 'state' | 'detail'>): boolean =>
  c.kind !== 'ad-hoc' && (c.state === 'todo' || c.state === 'snoozed' || (c.state === 'done' && detailN(c.detail, 'owed') > 0));

/**
 * A CARD CLOSED BECAUSE ITS WORK ENDED: done, explained by the lifecycle's row
 * (`done_by` whoever archived or deleted, `done_at` when; the pass's time when
 * the row keeps none), with `detail.ended` the why and `closedOn` its day,
 * nothing left owed. Its line names the brand and who ("closed: Organic India
 * was archived by Dana"), and says so when the card was asleep. Null: the card
 * is not one to close (`endsWithItsWork`).
 */
export function closeEnded(card: CardRow, end: EndedWork, brandName: string, nameOf: (email: string) => string, day: string, at: number): CardStep | null {
  if (!endsWithItsWork(card)) return null;
  const when = end.at ?? at;
  const by = { actor: end.actor, at: when, via: 'app' as const };
  const detail: CardDetail = { ...(card.detail ?? {}), owed: 0, ended: end.why, closedOn: operationsDay(when) };
  const next = card.state === 'snoozed'
    ? closedAsleep({ ...card, detail }, by, day)
    : { ...card, state: 'done' as const, detail, doneBy: by.actor, doneVia: by.via, doneAt: by.at };
  const words = endedWords(end.why, brandName, end.actor ? nameOf(end.actor) : null);
  const note = card.state === 'snoozed' ? `closed while snoozed${untilWords(card)}: ${words}` : `closed: ${words}`;
  const shot = card.state === 'snoozed' ? snoozeSnap : snap;
  return { card: next, events: [{ verb: 'done', actor: end.actor, at, from: shot(card), to: { ...shot(next), done_via: 'app', ended: end.why }, note }], closedDay: null };
}

// ─── A person's moves ───────────────────────────────────────────────────────

/**
 * What a person can do to a card from the inbox, beside the work itself. `done`
 * and `reopen` are an ad-hoc card's alone: every other kind is done by its
 * work. `escalate` hands the card to another seat.
 */
export type HandMove = 'snooze' | 'unsnooze' | 'archive' | 'unarchive' | 'done' | 'reopen' | 'escalate';
export const HAND_MOVES: readonly HandMove[] = ['snooze', 'unsnooze', 'archive', 'unarchive', 'done', 'reopen', 'escalate'];
export const isHandMove = (v: unknown): v is HandMove => typeof v === 'string' && (HAND_MOVES as readonly string[]).includes(v);

/** Why a move was refused, as a code the route words. */
export type MoveRefusal = 'cannot_snooze' | 'not_todo' | 'bad_day' | 'same_day' | 'not_snoozed' | 'archived' | 'not_archived' | 'not_a_task' | 'not_done' | 'bad_seat' | 'same_seat' | 'inactive';

/** The longest note an escalation carries. */
export const ESCALATE_NOTE_MAX = 1000;

/**
 * WHICH KINDS SNOOZE: an ad-hoc task (put off to a later day), an action item
 * to complete (the same, for one item), and the daily counting cards (skip the
 * queue for a day or two). A weekly or other event card's date is its work's,
 * and a snooze would only hide that it is late.
 */
export function canSnooze(kind: string): boolean {
  const c = kindDef(kind)?.cadence;
  return c === 'daily' || c === 'ad-hoc' || kind === 'complete-action-item';
}

/** A snooze moves the due day of a card that goes late (a task, an item to complete); a daily card keeps its day, and the roll closes it. */
const snoozeMovesDue = (kind: string): boolean => kindDef(kind)?.goesLate === true;

/** Is `s` a day, YYYY-MM-DD, that the calendar has? */
export function isDay(s: unknown): s is string {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

export interface MoveBy {
  /** Who, as an email (the history line's actor and the card's `archived_by` / `done_by`). */
  actor: string;
  /** Their first name, for the line's words: "archived by Riley". */
  who: string;
  at: number;
  /** The operations day. */
  today: string;
  /** Snooze only: the day it comes back, after today. */
  until?: string;
  /** Escalate only: the seat it goes to, and its name for the line ("Lead · Dana's pod"). */
  to?: { seat: string; label: string };
  /** Escalate only: what the seat it goes to should know. */
  note?: string | null;
}

/**
 * ONE PERSON'S MOVE ON ONE CARD, as the card it leaves and its history line.
 * Every line says what happened and who did it, in words a reader of the
 * card's timeline needs no code to follow: "snoozed by Riley until Nov 6",
 * "archived by Riley", "brought back by Riley", "done by Riley".
 *
 *   snooze     a to-do card of a kind that snoozes, until a day after today,
 *              for so many days; a task's or an item-to-complete's due day
 *              moves to it, since putting it off is what the snooze means (a
 *              daily card's day stays: the roll closes it). On a card already
 *              snoozed it moves the snooze to the new day (`snooze-moved`),
 *              and the due day with it when the snooze had set it
 *   unsnooze   back to do now; a card whose due day the snooze set is due today
 *   archive    any card not archived; what it was is kept, for unarchive
 *   unarchive  back as it was when archived: done, still snoozed, or to do
 *   done       an ad-hoc task, to do or snoozed: done by the person
 *   reopen     an ad-hoc task done: to do again
 *   escalate   a to-do card, to another seat: `escalated_from` keeps the seat
 *              it first left, which still sees it apart; sent on again it keeps
 *              that one, and sent back there it is that seat's own once more
 */
export function moveByHand(card: CardRow, move: HandMove, by: MoveBy): { step: CardStep } | { refused: MoveRefusal } {
  let next: CardRow;
  let note: string;
  switch (move) {
    case 'snooze': {
      if (!canSnooze(card.kind)) return { refused: 'cannot_snooze' };
      if (card.state !== 'todo' && card.state !== 'snoozed') return { refused: card.state === 'archived' ? 'archived' : 'not_todo' };
      if (!isDay(by.until) || by.until <= by.today) return { refused: 'bad_day' };
      const until = by.until;
      const days = daysBetweenUtc(by.today, until);
      if (card.state === 'snoozed') {
        if (card.snoozedUntil === until) return { refused: 'same_day' };
        // The due day follows the snooze only where the snooze had set it.
        const dueOn = snoozeMovesDue(card.kind) && card.snoozedUntil !== null && card.dueOn === card.snoozedUntil ? until : card.dueOn;
        next = { ...card, snoozedUntil: until, dueOn };
        note = `snooze moved by ${by.who}${card.snoozedUntil === null ? '' : ` from ${formatShort(card.snoozedUntil)}`} to ${formatShort(until)}, ${daysWords(days)} from today`;
        return { step: { card: next, events: [{ verb: 'snooze-moved', actor: by.actor, at: by.at, from: snoozeSnap(card), to: { ...snoozeSnap(next), days }, note }], closedDay: null } };
      }
      const dueOn = snoozeMovesDue(card.kind) && until > card.dueOn ? until : card.dueOn;
      next = { ...card, state: 'snoozed', snoozedUntil: until, dueOn };
      note = `snoozed by ${by.who} for ${daysWords(days)}, until ${formatShort(until)}`;
      return { step: { card: next, events: [{ verb: 'snoozed', actor: by.actor, at: by.at, from: snoozeSnap(card), to: { ...snoozeSnap(next), days }, note }], closedDay: null } };
    }
    case 'unsnooze': {
      if (card.state !== 'snoozed') return { refused: 'not_snoozed' };
      const dueOn = snoozeMovesDue(card.kind) && card.dueOn > by.today && card.dueOn === card.snoozedUntil ? by.today : card.dueOn;
      next = { ...card, state: 'todo', snoozedUntil: null, dueOn };
      note = `brought back from snooze by ${by.who}${card.snoozedUntil === null ? '' : `, before ${formatShort(card.snoozedUntil)}`}`;
      return { step: { card: next, events: [{ verb: 'unsnoozed', actor: by.actor, at: by.at, from: snoozeSnap(card), to: snoozeSnap(next), note }], closedDay: null } };
    }
    case 'archive':
      if (card.state === 'archived') return { refused: 'archived' };
      next = { ...card, state: 'archived', archivedAt: by.at, archivedBy: by.actor };
      note = `archived by ${by.who}${card.state === 'snoozed' ? ` while snoozed${untilWords(card)}` : ''}`;
      break;
    case 'unarchive': {
      if (card.state !== 'archived') return { refused: 'not_archived' };
      // What it was is in the row still: a done card keeps its done line, a snooze its day.
      const stillSnoozed = card.snoozedUntil !== null && card.snoozedUntil > by.today;
      const state: CardState = card.doneAt !== null ? 'done' : stillSnoozed ? 'snoozed' : 'todo';
      next = { ...card, state, archivedAt: null, archivedBy: null, snoozedUntil: state === 'snoozed' ? card.snoozedUntil : null };
      note = `brought back by ${by.who}`;
      break;
    }
    case 'done':
      if (card.kind !== 'ad-hoc') return { refused: 'not_a_task' };
      if (card.state !== 'todo' && card.state !== 'snoozed') return { refused: card.state === 'archived' ? 'archived' : 'not_todo' };
      next = { ...card, state: 'done', snoozedUntil: null, doneBy: by.actor, doneVia: 'app', doneAt: by.at };
      note = `done by ${by.who}${card.state === 'snoozed' ? ` while snoozed${untilWords(card)}` : ''}`;
      break;
    case 'reopen':
      if (card.kind !== 'ad-hoc') return { refused: 'not_a_task' };
      if (card.state !== 'done') return { refused: 'not_done' };
      next = reopen(card);
      note = `reopened by ${by.who}`;
      break;
    case 'escalate': {
      if (card.state !== 'todo') return { refused: card.state === 'archived' ? 'archived' : 'not_todo' };
      const to = by.to;
      if (to === undefined || to.seat === '') return { refused: 'bad_seat' };
      if (to.seat === card.seat) return { refused: 'same_seat' };
      const home = card.escalatedFrom ?? card.seat;
      next = { ...card, seat: to.seat, escalatedFrom: to.seat === home ? null : home };
      const said = (by.note ?? '').trim().slice(0, ESCALATE_NOTE_MAX);
      note = `escalated by ${by.who} to ${to.label}${said ? `: ${said}` : ''}`;
      break;
    }
  }
  // An escalation's line keeps the seat it left as well as where it went; a move off a sleeping card, its snooze's day.
  const shot = (c: CardRow): Record<string, unknown> => (move === 'escalate' ? { ...snap(c), escalated_from: c.escalatedFrom } : card.state === 'snoozed' ? snoozeSnap(c) : snap(c));
  const verb = move === 'archive' ? 'archived' : move === 'unarchive' ? 'unarchived' : move === 'done' ? 'done' : move === 'escalate' ? 'escalated' : 'reopened';
  return {
    step: {
      card: next,
      events: [{ verb, actor: by.actor, at: by.at, from: shot(card), to: next.state === 'done' ? { ...shot(next), done_via: 'app' } : shot(next), note }],
      closedDay: null,
    },
  };
}

/** A new ad-hoc task's card: to do, due on its day, on its seat; `addedBy` in its detail names who added it. */
export function newTask(t: {
  cardId: string;
  title: string;
  body: string | null;
  dueOn: string;
  brandSlug: string | null;
  workstream: string | null;
  seat: string;
  actor: string;
  who: string;
  at: number;
}): CardStep {
  const card: CardRow = {
    cardId: t.cardId,
    kind: 'ad-hoc',
    workstream: t.workstream,
    brandSlug: t.brandSlug,
    subject: t.cardId,
    seat: t.seat,
    escalatedFrom: null,
    dueOn: t.dueOn,
    state: 'todo',
    target: null,
    doneN: null,
    frozenAt: null,
    doneBy: null,
    doneVia: null,
    doneAt: null,
    snoozedUntil: null,
    archivedAt: null,
    archivedBy: null,
    detail: { addedBy: t.actor },
    createdAt: t.at,
    title: t.title,
    body: t.body,
  };
  return { card, events: [{ verb: 'made', actor: t.actor, at: t.at, from: null, to: { state: 'todo', due_on: t.dueOn, seat: t.seat }, note: `added by ${t.who}` }], closedDay: null };
}
